import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { Role } from "../constants";

const AuthContext = createContext(null);

const BASE_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:8000`;

async function fetchDelegation(token) {
  try {
    const res = await axios.get(`${BASE_URL}/auth/me/delegation`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data || null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [delegation, setDelegation] = useState(null);

  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    const token = localStorage.getItem("token");

    if (savedUser && token) {
      const userData = JSON.parse(savedUser);
      setUser(userData);

      if (userData.role === Role.ASM) {
        fetchDelegation(token).then((deleg) => {
          if (deleg) {
            localStorage.setItem("delegation", JSON.stringify(deleg));
            setDelegation(deleg);
          } else {
            localStorage.removeItem("delegation");
            setDelegation(null);
          }
          setLoading(false);
        });
      } else {
        localStorage.removeItem("delegation");
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const login = (userData, token) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);

    if (userData.role === Role.ASM) {
      fetchDelegation(token).then((deleg) => {
        if (deleg) {
          localStorage.setItem("delegation", JSON.stringify(deleg));
          setDelegation(deleg);
        } else {
          localStorage.removeItem("delegation");
          setDelegation(null);
        }
      });
    } else {
      localStorage.removeItem("delegation");
      setDelegation(null);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("delegation");
    setUser(null);
    setDelegation(null);
  };

  const setActiveDelegation = (deleg) => {
    if (deleg) {
      localStorage.setItem("delegation", JSON.stringify(deleg));
    } else {
      localStorage.removeItem("delegation");
    }
    setDelegation(deleg);
  };

  // ── Permission helpers ──────────────────────────────────────────────────────
  const isRole = (...roles) => roles.includes(user?.role);
  const isSuperAdmin = () => user?.is_super_admin === true;
  const isAdminPanel = () =>
    user?.is_admin === true ||
    isSuperAdmin() ||
    user?.role === Role.GSM ||
    user?.role === Role.ADMIN;
  const isDelegated = () => delegation !== null && user?.role === Role.ASM;
  const hasManagerPowers = () =>
    [Role.SUPER_ADMIN, Role.GSM, Role.DSM].includes(user?.role) ||
    isDelegated();

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        delegation,
        login,
        logout,
        setActiveDelegation,
        isRole,
        isSuperAdmin,
        isAdminPanel,
        isDelegated,
        hasManagerPowers,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}